import React, { useMemo, useState, useCallback } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import ProgressMeter from '../components/OwnerDiagnostic/ProgressMeter';
import ProfitGapQuestion from '../components/ProfitGapWizard/ProfitGapQuestion';
import ProfitGapResults from '../components/ProfitGapWizard/ProfitGapResults';

const GET_CURRENT_USER_FOR_PROFIT_GAP = gql`
  query GetCurrentUserForProfitGap {
    getCurrentUser {
      id
      fee_based_revenue
      trailer_revenue
      direct_revenue
      transactional_revenue
      recurring_revenue
      readiness_goal_type
      readiness_goal_lump_sum
      readiness_goal_annual_income
      readiness_withdrawal_rate
      readiness_current_investable_assets
      normalized_operating_expenses
    }
  }
`;

const UPDATE_USER_EXPENSES = gql`
  mutation UpdateUserExpenses($normalized_operating_expenses: Float) {
    updateCurrentUser(normalized_operating_expenses: $normalized_operating_expenses)
  }
`;

const Inner = ({ company }) => {
  const { data, loading, error } = useQuery(GET_CURRENT_USER_FOR_PROFIT_GAP, { fetchPolicy: 'cache-and-network' });
  const [updateUser] = useMutation(UPDATE_USER_EXPENSES);
  const [step, setStep] = useState(0);

  const user = data?.getCurrentUser || {};
  const vertical = (company && company.business_type) || 'wealth_rj';

  const [values, setValues] = useState({
    // Revenue components
    fee_based_revenue: 0,
    trailer_revenue: 0,
    direct_revenue: 0,
    transactional_revenue: 0,
    recurring_revenue: 0,
    non_recurring_revenue: 0,
    // Profit inputs
    normalized_operating_expenses: 0,
  });

  // Initialize values from user once
  React.useEffect(() => {
    if (!user || !user.id) return;
    setValues((prev) => ({
      ...prev,
      fee_based_revenue: user.fee_based_revenue || 0,
      trailer_revenue: user.trailer_revenue || 0,
      direct_revenue: user.direct_revenue || 0,
      transactional_revenue: user.transactional_revenue || 0,
      recurring_revenue: user.recurring_revenue || 0,
      non_recurring_revenue: (user.non_recurring_revenue || user.transactional_revenue || 0),
      normalized_operating_expenses: user.normalized_operating_expenses || 0,
    }));
  }, [user?.id]);

  const onChange = useCallback((patch) => setValues((p) => ({ ...p, ...patch })), []);

  // Persist expenses when they change (debounced via step navigation in UI)
  React.useEffect(() => {
    if (!user || !user.id) return;
    if (values.normalized_operating_expenses != null) {
      updateUser({ variables: { normalized_operating_expenses: Number(values.normalized_operating_expenses || 0) } }).catch(()=>{});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, values.normalized_operating_expenses]);

  const totals = useMemo(() => {
    const total_revenue = vertical === 'p_and_c'
      ? (values.recurring_revenue || 0) + (values.non_recurring_revenue || 0)
      : (values.fee_based_revenue || 0) + (values.trailer_revenue || 0) + (values.direct_revenue || 0) + (values.transactional_revenue || 0);

    const recasted_ebitda = Number(total_revenue) - Number(values.normalized_operating_expenses || 0);
    const ebitda_margin_current = total_revenue > 0 ? (recasted_ebitda / total_revenue) : 0.0;
    return { total_revenue, recasted_ebitda, ebitda_margin_current };
  }, [values, vertical]);

  const low_margin = (company && company.ebitda_margin_low != null) ? parseFloat(company.ebitda_margin_low) : 0.15;
  const high_margin = (company && company.ebitda_margin_high != null) ? parseFloat(company.ebitda_margin_high) : 0.45;
  const avg_margin = (company && company.ebitda_margin_avg != null) ? parseFloat(company.ebitda_margin_avg) : 0.228;
  const benchmarks = useMemo(() => {
    const benchmark_ebitda_low = totals.total_revenue * low_margin;
    const benchmark_ebitda_high = totals.total_revenue * high_margin;
    return { low_margin, high_margin, avg_margin, benchmark_ebitda_low, benchmark_ebitda_high };
  }, [totals.total_revenue, low_margin, high_margin, avg_margin]);

  const profit_gaps = useMemo(() => ({
    profit_gap_low: benchmarks.benchmark_ebitda_low - totals.recasted_ebitda,
    profit_gap_high: benchmarks.benchmark_ebitda_high - totals.recasted_ebitda,
  }), [benchmarks.benchmark_ebitda_low, benchmarks.benchmark_ebitda_high, totals.recasted_ebitda]);

  const next = () => setStep((s) => Math.min(s + 1, 2));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;

  if (step === 2) {
    return (
      <div>
        <div className="px-4 md:px-0 max-w-3xl mx-auto mt-2">
          <ProgressMeter current={2} total={3} />
        </div>
        <ProfitGapResults
          vertical={vertical}
          values={values}
          totals={totals}
          benchmarks={benchmarks}
          profit_gaps={profit_gaps}
          onBack={back}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 md:px-0 max-w-3xl mx-auto mt-2">
        <ProgressMeter current={Math.min(step, 2)} total={3} />
      </div>
      <ProfitGapQuestion
        step={step}
        vertical={vertical}
        values={values}
        totals={totals}
        onChange={onChange}
        onNext={next}
        onBack={back}
      />
    </div>
  );
};

const ProfitGapWizardContainer = (props) => (
  <ApolloProvider client={client}>
    <Inner company={props.company || {}} />
  </ApolloProvider>
);

export default ProfitGapWizardContainer;


